/**
 * Interview History — past sessions + progress analytics.
 * Premium sees the full trend + advanced analytics; Free/Pro see history only.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Bot, TrendingUp, Clock, Crown, Sparkles } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { useEntitlements } from "@/context/EntitlementsContext";

export default function InterviewHistory() {
  const { isPremium } = useEntitlements();
  const [sessions, setSessions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/realtime/sessions").then((r) => setSessions(r.data.sessions || [])).catch(() => {}),
      isPremium ? api.get("/realtime/analytics").then((r) => setAnalytics(r.data)).catch(() => {}) : Promise.resolve(),
    ]).finally(() => setLoading(false));
  }, [isPremium]);

  return (
    <div data-testid="interview-history" className="max-w-5xl">
      <PageHeader
        eyebrow="Interview archive"
        title="Your interview practice history."
        subtitle="Review scorecards, spot recurring weaknesses, watch yourself improve."
      />

      {isPremium && analytics?.trend?.length > 0 && (
        <AnalyticsBlock analytics={analytics} />
      )}

      {!isPremium && (
        <div className="mb-6 rounded-lg bg-primary/5 border border-primary/20 p-4 flex items-center justify-between" data-testid="premium-analytics-teaser">
          <div className="flex items-center gap-2 text-sm">
            <Crown size={14} className="text-primary" />
            <span>Premium unlocks trend charts, weakness clusters and personalized coaching recommendations.</span>
          </div>
          <Button asChild size="sm" variant="outline"><Link to="/app/pricing">Upgrade</Link></Button>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
          {sessions.length} session{sessions.length === 1 ? "" : "s"}
        </div>
        <Button asChild size="sm"><Link to="/app/interview/live">Start new interview</Link></Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : sessions.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-8 text-center text-muted-foreground">
          <Bot size={32} className="mx-auto mb-3 opacity-40" />
          You haven't run any voice interviews yet. Start your first one to see it here.
        </div>
      ) : (
        <div className="grid gap-3">
          {sessions.map((s) => <SessionRow key={s.id} s={s} />)}
        </div>
      )}
    </div>
  );
}

function AnalyticsBlock({ analytics }) {
  const trend = analytics.trend || [];
  const overallTrend = trend.filter((r) => r.overall != null).map((r) => r.overall);
  const lastAvg = overallTrend.slice(-5).reduce((a, b) => a + b, 0) / (overallTrend.slice(-5).length || 1);
  return (
    <div className="grid md:grid-cols-4 gap-3 mb-6" data-testid="premium-analytics">
      <StatCard label="Sessions" value={analytics.sessions_completed} icon={<Sparkles size={14} />} />
      <StatCard label="Total minutes" value={analytics.total_minutes} icon={<Clock size={14} />} />
      <StatCard label="Recent avg score" value={Math.round(lastAvg) || "—"} icon={<TrendingUp size={14} />} />
      <StatCard label="Latest verdict" value={analytics.latest_scorecard?.verdict || "—"} icon={<Bot size={14} />} />
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

function SessionRow({ s }) {
  const score = s.scorecard?.overall_score;
  const date = s.started_at ? new Date(s.started_at).toLocaleString() : "";
  return (
    <Link to={`/app/interview/session/${s.id}`} className="block" data-testid={`session-row-${s.id}`}>
      <div className="bg-card border border-border rounded-xl p-4 hover:border-primary/40 transition-colors flex items-center gap-4">
        <div className="h-12 w-12 rounded-lg flex items-center justify-center bg-primary/10 text-primary">
          <Bot size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {s.interview_type} · {s.difficulty}
            {s.role && <span className="text-muted-foreground"> · {s.role}</span>}
            {s.company && <span className="text-muted-foreground"> @ {s.company}</span>}
          </div>
          <div className="text-xs text-muted-foreground">{date} · {Math.round((s.duration_seconds || 0) / 60)}m · {s.persona}</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black">{score ?? "—"}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{s.status}</div>
        </div>
      </div>
    </Link>
  );
}
